// Helper function to get tracking identifiers with error handling and retry
async function getTrackingHeaders() {
  try {
    // First attempt to get attributes
    let attributes = window.ShopifyAnalytics?.lib?.trekkie?.defaultAttributes;
    
    // If not available, wait a bit and try again (analytics might still be loading)
    if (!attributes) {
      await new Promise(resolve => setTimeout(resolve, 500));
      attributes = window.ShopifyAnalytics?.lib?.trekkie?.defaultAttributes;
    }
    
    // If still not available, try one more time
    if (!attributes) {
      await new Promise(resolve => setTimeout(resolve, 500));
      attributes = window.ShopifyAnalytics?.lib?.trekkie?.defaultAttributes;
    }
    
    // Only send the unique token for recommendation tracking
    return {
      'X-Unique-Token': attributes?.uniqToken || '',
      'Content-Type': 'application/json'
    };
  } catch (error) {
    console.warn('Failed to extract tracking attributes:', error);
    // Return minimal headers if tracking data is unavailable
    return {
      'Content-Type': 'application/json'
    };
  }
}

// Generic API wrapper that includes tracking headers
async function apiRequest(url, options = {}) {
  const defaultHeaders = await getTrackingHeaders();
  
  const config = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers
    }
  };
  
  return await fetch(url, config);
}

document.addEventListener('DOMContentLoaded', () => {
  // Unique variables for the grouped version to prevent conflicts
  const makeSelectGrouped = document.getElementById('digb-make-select-grouped');
  const modelSelectGrouped = document.getElementById('digb-model-select-grouped');
  const searchButtonGrouped = document.getElementById('digb-search-by-digger-button-grouped');
  const searchTermInputGrouped = document.getElementById('digb-search-by-digger-term-grouped');
  const resultsContainerGrouped = document.getElementById('digb-search-by-digger-results-grouped');
  const loadingIndicatorGrouped = document.getElementById('digb-loading-indicator-grouped');

  const API_BASE_URL = 'https://api.hypv.ai/ws/digb/v1';

  // Store the last search results so we can re-render on resize
  let lastSearchResultsGrouped = [];

  // Get URL parameters (using standard parameter names)
  const urlParamsGrouped = new URLSearchParams(window.location.search);
  const initialMakeGrouped = urlParamsGrouped.get('make');
  const initialModelGrouped = urlParamsGrouped.get('model');
  const initialFilterGrouped = urlParamsGrouped.get('filter');

  const showLoadingGrouped = (show) => {
    loadingIndicatorGrouped.style.display = show ? 'block' : 'none';
  };

  // Function to update URL with current search parameters (using standard parameters)
  const updateURLGrouped = (make, model, filter) => {
    const url = new URL(window.location);
    const params = new URLSearchParams(url.search);

    // Clear existing parameters
    params.delete('make');
    params.delete('model');
    params.delete('filter');

    // Set new parameters if they exist
    if (make) params.set('make', make);
    if (model) params.set('model', model);
    if (filter) params.set('filter', filter);

    // Update URL without reloading the page
    url.search = params.toString();
    window.history.replaceState({}, '', url);
  };

  // Function to re-display products when screen size changes
  const reDisplayProductsGrouped = () => {
    if (lastSearchResultsGrouped.length > 0) {
      displayProductsGrouped(lastSearchResultsGrouped);
    }
  };

  // Add resize listener to handle mobile/desktop switching
  let resizeTimeoutGrouped;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeoutGrouped);
    resizeTimeoutGrouped = setTimeout(reDisplayProductsGrouped, 250);
  });

  const fetchMakesGrouped = async () => {
    try {
      showLoadingGrouped(true);
      const response = await apiRequest(`${API_BASE_URL}/makes/`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      // ADDED: Gracefully handle null or invalid response for makes
      const makes = data?.makes || [];

      makeSelectGrouped.innerHTML = '<option value="">Select Make</option>';
      makes.forEach(make => {
        const option = document.createElement('option');
        option.value = make.symbol;
        option.textContent = make.desc;
        makeSelectGrouped.appendChild(option);
      });

      // If there's an initial make parameter, check if it's valid before selecting it
      if (initialMakeGrouped && makes.some(m => m.symbol === initialMakeGrouped)) {
        makeSelectGrouped.value = initialMakeGrouped;
        // Trigger the change event to load models
        makeSelectGrouped.dispatchEvent(new Event('change'));
      } else if (initialMakeGrouped) {
        // If initial make is invalid, clear params and inform user
        console.warn(`Initial make "${initialMakeGrouped}" is not valid.`);
        updateURLGrouped(null, null, null); // Clear invalid params from URL
        resultsContainerGrouped.innerHTML = '<p>The selected make in the URL is not valid.</p>';
      }
    } catch (error) {
      console.error('Error fetching makes:', error);
      resultsContainerGrouped.innerHTML = '<p>Error loading makes. Please try again later.</p>';
      // Restore the background image when showing error message
      const container = document.querySelector('.digb-search-by-digger-grouped-container');
      if (container && !container.style.backgroundImage) {
        container.style.backgroundImage = 'url(\'https://static.hypv.com/c_digb/img/digb_white_watermark.jpg\')';
      }
    } finally {
      showLoadingGrouped(false);
    }
  };

  const fetchModelsGrouped = async (makeSymbol) => {
    if (!makeSymbol) {
      modelSelectGrouped.innerHTML = '<option value="">Select Make</option>';
      modelSelectGrouped.disabled = true;
      searchTermInputGrouped.disabled = true;
      return;
    }

    searchTermInputGrouped.disabled = false;
    try {
      showLoadingGrouped(true);
      const response = await apiRequest(`${API_BASE_URL}/models_for_make/?make=${makeSymbol}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      // CHANGE: Gracefully handle null or malformed API responses.
      const models = data?.models || [];

      if (models.length === 0) {
        modelSelectGrouped.innerHTML = '<option value="">No models found</option>';
        modelSelectGrouped.disabled = true;
        // If an initial model was specified, it's invalid, so show a message.
        if (initialModelGrouped && makeSymbol === initialMakeGrouped) {
          resultsContainerGrouped.innerHTML = '<p>The selected model in the URL is not valid for this make.</p>';
          // Restore the background image when showing error message
          const container = document.querySelector('.digb-search-by-digger-grouped-container');
          if (container && !container.style.backgroundImage) {
            container.style.backgroundImage = 'url(\'https://static.hypv.com/c_digb/img/digb_white_watermark.jpg\')';
          }
          updateURLGrouped(makeSymbol, null, null); // Clear invalid model param
        }
        return;
      }

      modelSelectGrouped.innerHTML = '<option value="">Select Model</option>';
      models.forEach(model => {
        const option = document.createElement('option');
        option.value = model.symbol;
        option.textContent = model.desc;
        modelSelectGrouped.appendChild(option);
      });
      modelSelectGrouped.disabled = false;

      // If there's an initial model parameter, check if it's valid before selecting it
      if (initialModelGrouped && makeSymbol === initialMakeGrouped && models.some(m => m.symbol === initialModelGrouped)) {
        modelSelectGrouped.value = initialModelGrouped;
        // Auto-trigger search if both make and model are valid
        setTimeout(() => {
          searchTermInputGrouped.disabled = false;
          if (initialFilterGrouped) {
            searchTermInputGrouped.value = initialFilterGrouped;
          }
          fetchProductsGrouped(initialModelGrouped, initialFilterGrouped || '');
        }, 100);
      } else if (initialModelGrouped && makeSymbol === initialMakeGrouped) {
        console.warn(`Initial model "${initialModelGrouped}" is not valid for make "${makeSymbol}".`);
        updateURLGrouped(makeSymbol, null, null); // Clear invalid model param
      }

    } catch (error) {
      console.error('Error fetching models:', error);
      resultsContainerGrouped.innerHTML = '<p>Error loading models. Please try again later.</p>';
      // Restore the background image when showing error message
      const container = document.querySelector('.digb-search-by-digger-grouped-container');
      if (container && !container.style.backgroundImage) {
        container.style.backgroundImage = 'url(\'https://static.hypv.com/c_digb/img/digb_white_watermark.jpg\')';
      }
    } finally {
      showLoadingGrouped(false);
    }
  };

  const fetchProductsGrouped = async (modelSymbol, searchTerm) => {
    if (!modelSymbol) {
      resultsContainerGrouped.innerHTML = '<p>Please select a make and model.</p>';
      return;
    }

    try {
      showLoadingGrouped(true);
      resultsContainerGrouped.innerHTML = '';
      // CHANGE: Use the new grouped API endpoint
      const response = await apiRequest(`${API_BASE_URL}/find_prd_grouped?model=${modelSymbol}&search_term=${encodeURIComponent(searchTerm)}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      // CHANGE: Handle the new grouped data structure
      const sections = data?.sections || [];

      // Flatten all products from all sections for Shopify data fetching
      let allProducts = [];
      sections.forEach(section => {
        if (section.products && Array.isArray(section.products)) {
          allProducts = allProducts.concat(section.products);
        }
      });

      const shopifyProducts = await getShopifyProductsGrouped(allProducts);
      
      // Create a map for quick lookup of Shopify data
      const shopifyDataMap = {};
      shopifyProducts.forEach(product => {
        if (product.ext_handle) {
          shopifyDataMap[product.ext_handle] = product;
        }
      });

      // Add Shopify data to each product in sections
      const sectionsWithShopifyData = sections.map(section => {
        if (section.products && Array.isArray(section.products)) {
          const productsWithShopifyData = section.products.map(product => {
            if (product.ext_handle && shopifyDataMap[product.ext_handle]) {
              return {
                ...product,
                ...shopifyDataMap[product.ext_handle]
              };
            }
            return {
              ...product,
              shopifyData: null,
              availableForSale: true
            };
          });
          return {
            ...section,
            products: productsWithShopifyData
          };
        }
        return section;
      });

      displayProductsGrouped(sectionsWithShopifyData);

      // Update URL with current search parameters
      const makeSymbol = makeSelectGrouped.value;
      updateURLGrouped(makeSymbol, modelSymbol, searchTerm);
    } catch (error) {
      console.error('Error fetching products:', error);
      resultsContainerGrouped.innerHTML = '<p>Error loading products. Please try again later.</p>';
      // Restore the background image when showing error message
      const container = document.querySelector('.digb-search-by-digger-grouped-container');
      if (container && !container.style.backgroundImage) {
        container.style.backgroundImage = 'url(\'https://static.hypv.com/c_digb/img/digb_white_watermark.jpg\')';
      }
    } finally {
      showLoadingGrouped(false);
      // Remove the background image once products are loaded
      removeBackgroundImage();
    }
  };

  const getShopifyProductsGrouped = async (products) => {
    // Extract handles from products that have them
    const handles = products
      .filter(product => product.ext_handle)
      .map(product => product.ext_handle);

    if (handles.length === 0) {
      // If no handles, return original products with default values
      return products.map(product => ({
        ...product,
        shopifyData: null,
        availableForSale: true // Default to true if we can't verify
      }));
    }

    // Create a map of handle to product for easy lookup
    const productMap = {};
    products.forEach(product => {
      if (product.ext_handle) {
        productMap[product.ext_handle] = product;
      }
    });

    try {
      // Since we can't use GraphQL on the frontend, we'll use the REST API
      // We'll make individual requests for each product to get detailed data
      const shopifyProductPromises = handles.map(async (handle) => {
        try {
          const response = await fetch(`/products/${handle}.js`);
          if (!response.ok) {
            return null;
          }
          const productData = await response.json();
          return {
            handle: handle,
            id: productData.id,
            title: productData.title,
            available: productData.available,
            price: productData.price,
            compare_at_price: productData.compare_at_price,
            featured_image: productData.featured_image
          };
        } catch (error) {
          console.error(`Error fetching Shopify data for ${handle}:`, error);
          return null;
        }
      });

      const shopifyProducts = await Promise.all(shopifyProductPromises);

      // Merge Shopify data with original products
      const mergedProducts = products.map(product => {
        if (!product.ext_handle) {
          return {
            ...product,
            shopifyData: null,
            availableForSale: true // Default to true if no handle
          };
        }

        // Find the matching Shopify product
        const shopifyProduct = shopifyProducts.find(p => p && p.handle === product.ext_handle);

        return {
          ...product,
          shopifyData: shopifyProduct || null,
          availableForSale: shopifyProduct ? shopifyProduct.available : false
        };
      });

      return mergedProducts;
    } catch (error) {
      console.error('Error fetching Shopify product data:', error);
      // Return original products with default values if Shopify fetch fails
      return products.map(product => ({
        ...product,
        shopifyData: null,
        availableForSale: true // Default to true if Shopify fetch fails
      }));
    }
  };


  const displayProductsGrouped = (sections) => {
    // Store the results for potential re-rendering on resize
    lastSearchResultsGrouped = sections;

    // Check if we have any sections with products
    let hasProducts = false;
    sections.forEach(section => {
      if (section.products && Array.isArray(section.products) && section.products.length > 0) {
        // Filter out products that are not available for sale
        const availableProducts = section.products.filter(product => product.availableForSale);
        if (availableProducts.length > 0) {
          hasProducts = true;
        }
      }
    });

    if (!hasProducts) {
      resultsContainerGrouped.innerHTML = '<p class="text-center">No products found for this selection.</p>';
      lastSearchResultsGrouped = []; // CHANGE: Clear last results if none were found
      // Restore the background image when no products are found
      const container = document.querySelector('.digb-search-by-digger-grouped-container');
      if (container && !container.style.backgroundImage) {
        container.style.backgroundImage = 'url(\'https://static.hypv.com/c_digb/img/digb_white_watermark.jpg\')';
      }
      return;
    }

    // Generate HTML for each section
    let sectionsHTML = '';
    
    sections.forEach(section => {
      // Only display sections that have products
      if (section.products && Array.isArray(section.products) && section.products.length > 0) {
        // Filter out products that are not available for sale
        const availableProducts = section.products.filter(product => product.availableForSale);
        
        // Only display section if it has available products
        if (availableProducts.length > 0) {
          // Add section heading
          sectionsHTML += `<h2 class="section-heading">${section.type}</h2>`;
          
          // Generate product list for this section
          const productListHTML = availableProducts.map(product => {
            // Use the new json_card field if available, otherwise fallback to previous logic
            if (product.json_card) {
              try {
                // Parse the json_card data
                const jsonCard = typeof product.json_card === 'string' ? JSON.parse(product.json_card) : product.json_card;

                const productHandle = jsonCard.handle;
                const productUrl = productHandle ? `/products/${productHandle}` : '#';
                const title = jsonCard.title || 'Untitled Product';

                // Get images
                const images = jsonCard.images && jsonCard.images.edges ? jsonCard.images.edges : [];
                const firstImage = images.length > 0 ? images[0].node : null;
                const secondImage = images.length > 1 ? images[1].node : null;

                // Check if we have hover image
                const hasHoverImage = !!(secondImage && firstImage &&
                  (firstImage.url_460 !== secondImage.url_460 ||
                    firstImage.url_320 !== secondImage.url_320));

                // Build image HTML
                let imageHTML = '';
                if (firstImage) {
                  // Create srcset for responsive images using higher resolution
                  const srcsetWidths = [320, 460, 600, 800, 900, 1200];
                  const srcsetArray = srcsetWidths.map(width => {
                    const urlKey = `url_${width}`;
                    return firstImage[urlKey] ? `${firstImage[urlKey].replace(/&amp;/g, '&')} ${width}w` : null;
                  }).filter(url => url !== null);

                  const srcset = srcsetArray.join(', ');
                  const sizes = "(min-width: 1628px) calc((1500px - 72px) / 4), (min-width: 1280px) calc((100vw - 200px) / 4), (min-width: 769px) calc((100vw - 136px) / 4), (min-width: 600px) calc((100vw - 112px) / 3), calc(100vw - 40px)";

                  // Create a clean title for alt text (remove quotes that might break HTML)
                  const cleanTitle = title.replace(/"/g, '&quot;');

                  // Use higher resolution image for src (800px instead of 460px)
                  const mainSrc = firstImage.url_800 || firstImage.url_900 || firstImage.url_600 || firstImage.url_460 || firstImage.url_320;

                  imageHTML = `
                  <img
                    data-srcset="${srcset}"
                    sizes="${sizes}"
                    data-src="${mainSrc}"
                    src="${mainSrc}"
                    class="img-fit img-fit--contain card__main-image no-js-hidden"
                    loading="lazy"
                    width="${firstImage.width || 1000}"
                    height="${firstImage.height || 1000}"
                    alt="${cleanTitle}">
                  <noscript>
                    <img
                      src="${mainSrc}"
                      loading="lazy"
                      class="img-fit img-fit--contain card__main-image"
                      width="${firstImage.width || 1000}"
                      height="${firstImage.height || 1000}"
                      alt="${cleanTitle}">
                  </noscript>
                `;

                  // Add hover image if available (but only on non-mobile devices)
                  if (secondImage && hasHoverImage) {
                    const hoverSrcsetArray = srcsetWidths.map(width => {
                      const urlKey = `url_${width}`;
                      return secondImage[urlKey] ? `${secondImage[urlKey].replace(/&amp;/g, '&')} ${width}w` : null;
                    }).filter(url => url !== null);

                    const hoverSrcset = hoverSrcsetArray.join(', ');

                    // Use higher resolution image for hover (800px instead of 460px)
                    const hoverSrc = secondImage.url_800 || secondImage.url_900 || secondImage.url_600 || secondImage.url_460 || secondImage.url_320;

                    imageHTML += `
                    <img
                      data-srcset="${hoverSrcset}"
                      sizes="${sizes}"
                      data-src="${hoverSrc}"
                      src="${hoverSrc}"
                      class="img-fit img-fit--contain card__hover-image no-js-hidden"
                      loading="lazy"
                      width="${secondImage.width || 1000}"
                      height="${secondImage.height || 1000}"
                      alt="${cleanTitle}">
                    <noscript>
                      <img
                        src="${hoverSrc}"
                        loading="lazy"
                        class="img-fit img-fit--contain card__hover-image"
                        width="${secondImage.width || 1000}"
                        height="${secondImage.height || 1000}"
                        alt="${cleanTitle}">
                    </noscript>
                  `;
                  }
                } else {
                  imageHTML = '<div class="media__placeholder img-fit"><svg class="placeholder-svg" viewBox="0 0 500 500" xmlns="http://www.w3.org/2000/svg"><rect fill="#f5f5f5" width="100%" height="100%"/><path d="M250 200a 50 50 0 1 0 0.00001 0z" fill="#e0e0e0"/><path d="M150 250 L350 250 L300 300 L200 300 Z" fill="#e0e0e0"/></svg></div>';
                }

                // Get price information
                let priceHTML = '';
                const variants = jsonCard.variants && jsonCard.variants.edges ? jsonCard.variants.edges : [];
                if (variants.length > 0) {
                  const firstVariant = variants[0].node;
                  const price = firstVariant.price;
                  const compareAtPrice = firstVariant.compareAtPrice;
                  const isOnSale = compareAtPrice && parseFloat(compareAtPrice) > parseFloat(price);

                  // Format prices
                  const formattedPrice = formatMoneyGrouped(price * 100);
                  const formattedCompareAtPrice = compareAtPrice ? formatMoneyGrouped(compareAtPrice * 100) : null;

                  priceHTML = `
                  <div class="price${isOnSale ? ' price--on-sale' : ''}">
                    <div class="price__default">
                      <strong class="price__current">${formattedPrice}</strong>
                      ${isOnSale ? `<s class="price__was">${formattedCompareAtPrice}</s>` : ''}
                    </div>
                  </div>
                `;

                  // Add VAT price
                  const vatRate = 1.2; // 20% VAT
                  const vatPrice = (price * vatRate).toFixed(2);
                  priceHTML += `<p style="font-size:1.5rem;">INC. VAT £${vatPrice}</p>`;
                } else {
                  priceHTML = '<div class="price"><div class="price__default"><strong class="price__current">Price not available</strong></div></div>';
                }

                // Check if we're on a mobile device
                const isMobile = window.matchMedia('(max-width: 768.98px)').matches;

                // Using the list-based product card structure
                return `
                <li class="js-pagination-result">
                  <product-card class="card card--product h-full relative flex">
                    <div class="card__media${hasHoverImage && !isMobile ? ' has-hover-image' : ''} image-blend relative">
                      <a href="${productUrl}" aria-label="${title.replace(/"/g, '&quot;')}" class="media block relative image-blend js-prod-link" tabindex="-1">
                        ${imageHTML}
                      </a>
                    </div>
                    <div class="card__info-container flex flex-col flex-auto relative">
                      <div class="card__info w-full">
                        <div class="card__info-inner inline-block w-full">
                          <p class="card__title font-bold mb-1">
                            <a href="${productUrl}" class="card-link text-current js-prod-link">
                              ${title}
                            </a>
                          </p>
                          ${priceHTML}
                        </div>
                      </div>
                    </div>
                  </product-card>
                </li>
              `;
              } catch (e) {
                console.error('Error parsing json_card:', e);
                // Fallback to previous implementation if json_card parsing fails
              }
            } else {
              // Fallback to previous implementation if json_card is not available
              const productHandle = product.ext_handle || (product.shopifyData ? product.shopifyData.handle : null);
              const productUrl = productHandle ? `/products/${productHandle}` : '#';

              // Image from Shopify if available, otherwise from API
              let imageUrl = product.image_url_primary || 'https://via.placeholder.com/500x500.png?text=No+Image';
              let imageAlt = 'Product image';
              if (product.shopifyData && product.shopifyData.featured_image) {
                imageUrl = product.shopifyData.featured_image;
                imageAlt = product.shopifyData.title;
              }

              // Title from Shopify if available, otherwise from API
              const title = (product.shopifyData ? product.shopifyData.title : null) || product.title || 'Untitled Product';

              // Price from Shopify if available, otherwise from API
              let priceHTML = '';
              if (product.shopifyData) {
                const price = product.shopifyData.price;
                const compareAtPrice = product.shopifyData.compare_at_price;
                const isOnSale = compareAtPrice && parseFloat(compareAtPrice) > parseFloat(price);

                priceHTML = `
                  <div class="price${isOnSale ? ' price--on-sale' : ''}">
                    <div class="price__default">
                      <strong class="price__current">${formatMoneyGrouped(price)}</strong>
                      ${isOnSale ? `<s class="price__was">${formatMoneyGrouped(compareAtPrice)}</s>` : ''}
                    </div>
                  </div>
                `;
              } else if (product.last_max_sell_price) {
                // Use the last_max_sell_price from the API
                priceHTML = `
                  <div class="price">
                    <div class="price__default">
                      <strong class="price__current">${formatMoneyGrouped(product.last_max_sell_price * 100)}</strong>
                    </div>
                  </div>
                `;
              } else if (product.price) {
                priceHTML = `
                  <div class="price">
                    <div class="price__default">
                      <strong class="price__current">${formatMoneyGrouped(product.price * 100)}</strong>
                    </div>
                  </div>
                `;
              } else {
                priceHTML = '<div class="price"><div class="price__default"><strong class="price__current">Price not available</strong></div></div>';
              }

              // Using the list-based product card structure
              return `
                <li class="js-pagination-result">
                  <div class="card card--product h-full card--no-lines relative flex">
                    <div class="card__media relative">
                      <a href="${productUrl}" class="media block relative js-prod-link" tabindex="-1">
                        <img src="${imageUrl}" alt="${imageAlt}" loading="lazy" class="img-fit card__main-image" width="500" height="500">
                      </a>
                    </div>
                    <div class="card__info-container flex flex-col flex-auto relative">
                      <div class="card__info w-full">
                        <div class="card__info-inner inline-block w-full">
                          <p class="card__title font-bold">
                            <a href="${productUrl}" class="card-link text-current js-prod-link">
                              ${title}
                            </a>
                          </p>
                          <div class="card__price theme-money">
                            ${priceHTML}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              `;
            }
          }).join('');

          sectionsHTML += `
            <ul class="digb-product-grid-grouped" role="list">
              ${productListHTML}
            </ul>
          `;
        }
      }
    });

    resultsContainerGrouped.innerHTML = sectionsHTML;
  };

  const formatMoneyGrouped = (cents) => {
    // Convert to dollars and format with 2 decimal places
    const dollars = (cents / 100).toFixed(2);
    // Format as currency - this is a simplified version
    // In a real implementation, you would use Shopify's actual money formatting
    return `£${dollars}`;
  };

  // Function to remove the background image from the container
  const removeBackgroundImage = () => {
    const container = document.querySelector('.digb-search-by-digger-grouped-container');
    if (container) {
      container.style.backgroundImage = 'none';
    }
  };

  // Handle make selection change
  makeSelectGrouped.addEventListener('change', (event) => {
    const makeSymbol = event.target.value;
    
    // Clear results when make changes
    resultsContainerGrouped.innerHTML = '';
    lastSearchResultsGrouped = [];
    
    // Restore the background image when results are cleared
    const container = document.querySelector('.digb-search-by-digger-grouped-container');
    if (container && !container.style.backgroundImage) {
      container.style.backgroundImage = 'url(\'https://static.hypv.com/c_digb/img/digb_white_watermark.jpg\')';
    }
    
    // Clear model selection
    modelSelectGrouped.innerHTML = '<option value="">Select Model</option>';
    modelSelectGrouped.disabled = true;
    
    // Clear search term
    searchTermInputGrouped.value = '';
    searchTermInputGrouped.disabled = true;
    
    // Update URL
    updateURLGrouped(makeSymbol, null, null);
    
    // Fetch models for selected make
    if (makeSymbol) {
      fetchModelsGrouped(makeSymbol);
    }
  });

  // Handle model selection change - auto-search when model changes
  modelSelectGrouped.addEventListener('change', (event) => {
    const modelSymbol = event.target.value;
    const makeSymbol = makeSelectGrouped.value;
    const searchTerm = searchTermInputGrouped.value || '';
    
    // Update URL
    updateURLGrouped(makeSymbol, modelSymbol, searchTerm);
    
    // If model is selected, auto-fetch products
    if (modelSymbol) {
      fetchProductsGrouped(modelSymbol, searchTerm);
    } else {
      // Clear results if no model selected
      resultsContainerGrouped.innerHTML = '';
      lastSearchResultsGrouped = [];
      // Restore the background image when results are cleared
      const container = document.querySelector('.digb-search-by-digger-grouped-container');
      if (container && !container.style.backgroundImage) {
        container.style.backgroundImage = 'url(\'https://static.hypv.com/c_digb/img/digb_white_watermark.jpg\')';
      }
    }
  });

  searchButtonGrouped.addEventListener('click', () => {
    const modelSymbol = modelSelectGrouped.value;
    const searchTerm = searchTermInputGrouped.value;
    fetchProductsGrouped(modelSymbol, searchTerm);
  });

  searchTermInputGrouped.addEventListener('keypress', (event) => {
    if (event.key === 'Enter') {
      const modelSymbol = modelSelectGrouped.value;
      const searchTerm = searchTermInputGrouped.value;
      fetchProductsGrouped(modelSymbol, searchTerm);
    }
  });

  // CHANGE: Restructured initial logic to be more robust
  const initializeSearchGrouped = async () => {
    await fetchMakesGrouped(); // This will now handle valid/invalid initialMake
  };

  initializeSearchGrouped();
});